/** Shared UI for Proyectos / Tareas / Tiempo (BHM-CLD-REQ-028). Obsidian Kinetic. */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { errText } from '../../stores/activityApi';
import {
  workApi,
  fmtClock,
  type TimerState,
  type TimeEntryRow,
  PRIORITY_CLS,
  PRIORITY_LABELS,
  TASK_STATUS_CLS,
  TASK_STATUS_LABELS,
  type TaskPriority,
  type TaskStatus,
} from '../../stores/workApi';

export function Icon({ name, className = '', filled = false }: { name: string; className?: string; filled?: boolean }) {
  return (
    <span aria-hidden="true" className={`material-symbols-outlined ${className}`} style={filled ? { fontVariationSettings: "'FILL' 1" } : undefined}>
      {name}
    </span>
  );
}

export const inputCls =
  'w-full rounded-lg border border-outline-variant bg-surface-container-high px-3 py-2 text-sm text-on-surface placeholder:text-outline focus:border-[#FF5E1E] focus:outline-none';
export const btnPrimary =
  'inline-flex items-center gap-2 rounded-lg bg-[#FF5E1E] px-4 py-2 text-sm font-medium text-white hover:bg-[#FF5E1E]/90 disabled:opacity-50';
export const btnGhost =
  'inline-flex items-center gap-2 rounded-lg border border-outline-variant px-3 py-2 text-sm text-on-surface-variant hover:bg-surface-container-highest disabled:opacity-50';

export function Modal({ title, onClose, children, wide = false }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onMouseDown={onClose}>
      <div role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => e.stopPropagation()}
        className={`flex max-h-[90vh] w-full flex-col rounded-xl border border-outline-variant bg-surface-container shadow-2xl ${wide ? 'max-w-4xl' : 'max-w-xl'}`}>
        <div className="flex items-center justify-between border-b border-outline-variant px-6 py-4">
          <h2 className="text-lg font-semibold text-on-surface">{title}</h2>
          <button onClick={onClose} aria-label="Cerrar" className="rounded-lg p-1.5 text-on-surface-variant hover:bg-surface-container-highest"><Icon name="close" /></button>
        </div>
        <div className="overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium uppercase tracking-wider text-on-surface-variant">{label}</span>
      {children}
    </label>
  );
}

export function Pill({ className, children }: { className: string; children: ReactNode }) {
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${className}`}>{children}</span>;
}

export function StatusPill({ status }: { status: TaskStatus }) {
  return <Pill className={TASK_STATUS_CLS[status]}>{TASK_STATUS_LABELS[status]}</Pill>;
}

export function PriorityLabel({ priority }: { priority: TaskPriority }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${PRIORITY_CLS[priority]}`}>
      <Icon name={priority === 'URGENT' ? 'priority_high' : 'flag'} className="text-sm" />
      {PRIORITY_LABELS[priority]}
    </span>
  );
}

export function ErrorBanner({ message, onClose }: { message: string | null; onClose: () => void }) {
  if (!message) return null;
  return (
    <div className="flex items-center gap-3 rounded-lg border border-[#F43F5E]/30 bg-[#F43F5E]/10 px-4 py-3" role="alert">
      <Icon name="error" className="text-sm text-[#F43F5E]" />
      <p className="flex-1 text-sm text-[#F43F5E]">{message}</p>
      <button onClick={onClose} aria-label="Cerrar error" className="text-[#F43F5E]"><Icon name="close" className="text-sm" /></button>
    </div>
  );
}

export function useToast() {
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);
  const node = toast ? (
    <div role="status" aria-live="polite" className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 rounded-lg border border-[#10B981]/40 bg-surface-container-highest px-4 py-2 text-sm font-medium text-on-surface shadow-xl">
      {toast}
    </div>
  ) : null;
  return { notify: setToast, node };
}

/** Live timer bar: Iniciar is done from a task/project; here Pausar / Reanudar / Finalizar / Descartar. */
export function TimerBar({ onStopped, refreshKey = 0 }: { onStopped?: (entry: TimeEntryRow) => void; refreshKey?: number }) {
  const [timer, setTimer] = useState<TimerState | null>(null);
  const [offset, setOffset] = useState(0);
  const [tick, setTick] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const t = await workApi.timer();
      setTimer(t || null);
      setOffset(t ? Date.now() - new Date(t.serverTime).getTime() : 0);
    } catch { setTimer(null); }
  }, []);
  useEffect(() => { void load(); }, [load, refreshKey]);
  useEffect(() => {
    if (timer?.state !== 'RUNNING') return;
    const i = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(i);
  }, [timer?.state]);

  if (!timer) return null;
  const elapsed = timer.state === 'RUNNING'
    ? timer.elapsedSec + Math.max(0, Math.floor((Date.now() - offset - new Date(timer.serverTime).getTime()) / 1000))
    : timer.elapsedSec;
  void tick;

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) { setError(errText(e)); }
    setBusy(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[#FF5E1E]/40 bg-gradient-to-r from-[#FF5E1E]/10 to-[#D946EF]/10 px-4 py-3" data-testid="timer-bar">
      <Icon name="timer" className={timer.state === 'RUNNING' ? 'animate-pulse text-[#FF5E1E]' : 'text-on-surface-variant'} />
      <span className="font-mono text-xl font-semibold text-on-surface" data-testid="timer-elapsed">{fmtClock(elapsed)}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-on-surface">{timer.taskTitle ?? 'Sin tarea'} <span className="text-on-surface-variant">· {timer.projectName}</span></p>
        <p className="text-xs text-on-surface-variant">{timer.state === 'RUNNING' ? 'En marcha' : 'En pausa'}</p>
      </div>
      {timer.state === 'RUNNING'
        ? <button className={btnGhost} disabled={busy} onClick={() => act(async () => setTimer(await workApi.pauseTimer()))}><Icon name="pause" className="text-base" />Pausar</button>
        : <button className={btnGhost} disabled={busy} onClick={() => act(async () => { const t = await workApi.resumeTimer(); setTimer(t); setOffset(Date.now() - new Date(t.serverTime).getTime()); })}><Icon name="play_arrow" className="text-base" />Reanudar</button>}
      <button className={btnPrimary} disabled={busy} onClick={() => act(async () => { const e = await workApi.stopTimer(); setTimer(null); onStopped?.(e); })}>
        <Icon name="stop" className="text-base" />Finalizar
      </button>
      <button className="text-xs text-outline hover:text-[#F43F5E]" disabled={busy}
        onClick={() => { if (window.confirm('¿Descartar el tiempo del temporizador?')) void act(async () => { await workApi.discardTimer(); setTimer(null); }); }}>
        Descartar
      </button>
      {error && <p className="w-full text-xs text-[#F43F5E]">{error}</p>}
      <Link to="/operaciones/tiempo" className="sr-only">Ir a Tiempo</Link>
    </div>
  );
}
